import React, { useState } from 'react';
import styled from 'styled-components';
import UserManagement from '../components/UserManagement';
import UserInfo from './UserInfo';
import { FaUser, FaTrash, FaUserCog } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const SettingsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 100px;
  background-color: #FAF9F6;
  min-height: 100vh;
`;

const SettingsBox = styled.div`
  background-color: white;
  width: 100%;
  max-width: 1000px;
  padding: 40px;
  border-radius: 0;
  box-shadow: none;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  color: ${props => props.active ? '#6a0dad' : '#333'};
  border-bottom: ${props => props.active ? '2px solid #6a0dad' : '2px solid transparent'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContentBox = styled.div`
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const SettingsTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 24px;
`;

const Button = styled.button`
  padding: 14px 28px;
  font-size: 18px;
  margin-left: -21px;
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: black;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const DeleteButton = styled.button`
  margin-top: 32px;
  padding: 14px 28px;
  font-size: 18px;
  background-color: red;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #cc0000;
  }
`;

const IconWrapper = styled.span`
  margin-right: 8px;
  display: flex;
`;

const Description = styled.p`
  margin-top: 8px;
  font-size: 14px;
  color: rgb(0, 0, 0);
  max-width: 400px;
  text-align: center;
  line-height: 1.4;
`;

const MotionWrapper = styled(motion.div)`
  width: 100%;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  text-align: center;
`;

const ModalActions = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  gap: 10px;

  button {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
  }
`;

const Settings = ({ user }) => {
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [activeTab, setActiveTab] = useState('userinfo');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      const response = await fetch('/api/delete_account', {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        setDeleting(false);
        window.location.href = '/'; // or '/login'
      } else {
        setDeleting(false);
        alert(data.error || "Failed to delete account.");
      }
    } catch (err) {
      console.error(err);
      setDeleting(false);
      alert("Something went wrong.");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'userinfo':
        return <UserInfo user={user} />;
      case 'management':
        return <UserManagement user={user} />;
      default:
        return null;
    }
  };

  

  return (
    <SettingsContainer>
      <SettingsBox>
        <SettingsTitle>Settings</SettingsTitle>

        {/* Tabs */}
        <Tabs>
          <Tab active={activeTab === 'userinfo'} onClick={() => setActiveTab('userinfo')}>
            <FaUser /> User Info
          </Tab>
          <Tab active={activeTab === 'management'} onClick={() => setActiveTab('management')}>
            <FaUserCog /> User Management
          </Tab>
        </Tabs>



        <ContentBox>
          {renderTabContent()}

          {/*CHANGE: Only show delete section in userinfo tab */}
          {activeTab === 'userinfo' && (
            <>
              {/* <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                <DeleteButton onClick={() => setShowDeleteDialog(true)}>
                  <FaTrash size={18} />
                  Delete My Account
                </DeleteButton>
              </div> */}

              <AnimatePresence>
                {showDeleteDialog && (
                  <ModalOverlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ModalContent
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                    >
                      <h3>Are you sure you want to delete your account?</h3>
                      <p>This action cannot be undone.</p>
                      <ModalActions>
                        <button onClick={() => setShowDeleteDialog(false)}>Cancel</button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deleting}
                          style={{
                            backgroundColor: 'red',
                            color: 'white',
                            padding: '0.5rem 1rem',
                            borderRadius: '5px',
                            border: 'none',
                          }}
                        >
                          {deleting ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                      </ModalActions>
                    </ModalContent>
                  </ModalOverlay>
                )}
              </AnimatePresence>
            </>
          )}
        </ContentBox>
      </SettingsBox>
    </SettingsContainer>
  );
};


export default Settings;
